import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// --- Interfaces ---
export interface HiveMessage {
  id?: string;
  message_id?: string;
  phoneNumber: string;
  message: string;
  direction: 'inbound' | 'outbound';
  status: string;
  timestamp?: Date;
  moduleType?: 'Contacts' | 'Leads' | 'Accounts';
  moduleId?: string;
  isTemplate?: boolean;
  message_type?: 'text' | 'image' | 'document' | 'video' | 'audio';
  media_url?: string;
  reply_to_message_id?: string;
}

export interface HiveTemplate {
  id: string;
  template_id: number;
  channel_id?: number;
  name: string;
  status: string;
  language?: string;
  category?: string;
  content?: string;
  parameters?: string | number;
  components?: Array<{
    type: 'BODY' | 'HEADER' | 'FOOTER' | 'BUTTONS';
    text?: string;
  }>;
}

export interface TemplateParameter {
  type: 'text' | 'image' | 'document' | 'video';
  parameter_name?: string;
  value: string;
}

export interface TemplateMapping {
  id: string;
  template_id: number;
  parameter_name: string;
  mapped_field: string;
  default_value: string;
}

export interface HiveChannel {
  channel_id: number;
  name: string;
  status: string;
  phone_number_id: string;
  phone_number?: string;
}

export interface RecordDetails {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class HiveMessageService {

  // 🔥 Helper: Build full URL for Controller Action (Relative Path)
  // This replaces the old direct PHP file paths with SuiteCRM standard action calls
  private buildApiUrl(action: string, params?: { [key: string]: string }): string {
    // Uses the current directory context, calling index.php
    let url = `legacy/index.php?module=Hive_Message&action=${action}`;
    
    if (params && Object.keys(params).length > 0) {
      const queryString = Object.keys(params)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
      url += `&${queryString}`;
    }
    
    return url;
  }

  // 🔥 Helper: Get Media Base URL from Sugar Config (for media URLs only)
  private mediaBaseUrl: string | null = null;
  private mediaBaseUrl$: Observable<string> | null = null;
  
  private getMediaBaseUrl(): Observable<string> {
    if (this.mediaBaseUrl) {
      return of(this.mediaBaseUrl);
    }

    if (this.mediaBaseUrl$) {
      return this.mediaBaseUrl$;
    }

    // Fetch site_url from Sugar config via API Action
    const settingsUrl = this.buildApiUrl('get_settings');
    
    this.mediaBaseUrl$ = this.http.get<any>(settingsUrl).pipe(
      map(response => {
        let baseUrl = response.site_url || '';
        
        if (!baseUrl) {
          const origin = window.location.origin;
          const pathname = window.location.pathname;
          let basePath = pathname.split('/#/')[0].split('/index.php')[0];
          if (!basePath.endsWith('/')) {
            basePath += '/';
          }
          baseUrl = `${origin}${basePath}`;
        } else {
          baseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
        }
        
        this.mediaBaseUrl = baseUrl;
        return baseUrl;
      }),
      catchError(() => {
        const fallbackUrl = `${window.location.origin}/`;
        this.mediaBaseUrl = fallbackUrl;
        return of(fallbackUrl);
      })
    );

    return this.mediaBaseUrl$;
  }

  private cachedTemplates: HiveTemplate[] | null = null;

  constructor(private http: HttpClient) {
    this.preloadTemplates();
  }

  // --- Helper: Preload Templates ---
  private preloadTemplates(): void {
    // Calls the action_GetLocalTemplates in controller.php
    const url = this.buildApiUrl('GetLocalTemplates');
    this.http.get<any>(url).pipe(
      map(response => response.data || []),
      catchError(() => of([]))
    ).subscribe(templates => {
      this.cachedTemplates = templates;
    });
  }

  // --- Helper: Clean Phone Number ---
  private cleanPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return '';
    let cleaned = phoneNumber.replace(/[\s()\-]/g, '');
    return cleaned.startsWith('+') ? cleaned : '+' + cleaned;
  }

  // --- 1. Get Conversation History ---
  getConversationHistory(phoneNumber: string): Observable<HiveMessage[]> {
    const targetPhone = this.cleanPhoneNumber(phoneNumber);
    // Calls action_GetMessages
    const url = this.buildApiUrl('GetMessages', { phone_number: targetPhone });

    return this.http.get<any>(url).pipe(
      map(res => {
        const messages = res.data || [];
        return messages.map((msg: any) => {
          let normalizedStatus = (msg.status || 'sent').toLowerCase().trim();
          
          if (normalizedStatus === 'pending' || normalizedStatus === 'queued') normalizedStatus = 'sent';
          else if (normalizedStatus === 'failed' || normalizedStatus === 'error') normalizedStatus = 'failed';
          else if (normalizedStatus === 'read' || normalizedStatus === 'readed') normalizedStatus = 'read';
          else if (normalizedStatus === 'delivered' || normalizedStatus === 'delivery') normalizedStatus = 'delivered';
          
          return {
            id: msg.id,
            message_id: msg.message_id,
            phoneNumber: msg.phoneNumber,
            message: msg.message || '',
            direction: msg.direction,
            status: normalizedStatus,
            timestamp: new Date(msg.timestamp),
            message_type: msg.message_type || 'text',
            media_url: msg.media_url || '',
            reply_to_message_id: msg.reply_to_message_id || ''
          };
        });
      }),
      catchError(() => of([]))
    );
  }

  // --- 2. Get Templates ---
  getTemplates(channelId?: number): Observable<HiveTemplate[]> {
    const params: { [key: string]: string } = {};
    if (channelId) {
      params['channel_id'] = channelId.toString();
    }
    // Calls action_GetLocalTemplates
    const url = this.buildApiUrl('GetLocalTemplates', Object.keys(params).length > 0 ? params : undefined);
    
    return this.http.get<any>(url).pipe(
      map(response => response.data || []),
      catchError(() => of([]))
    );
  }

  // --- 3. Get Template Mappings ---
  getTemplateMappings(templateId: string | number, module: string): Observable<TemplateMapping[]> {
    // Calls action_GetTemplateMappings
    const url = this.buildApiUrl('GetTemplateMappings', {
      template_id: templateId.toString(),
      module: module
    });
    return this.http.get<any>(url).pipe(
      map(response => response.data || []),
      catchError(() => of([]))
    );
  }

  // --- 4. Get Channels ---
  getChannels(): Observable<HiveChannel[]> {
    // Calls action_GetLocalChannels
    const url = this.buildApiUrl('GetLocalChannels');
    return this.http.get<any>(url).pipe(
      map(res => res.data || []),
      catchError(() => of([]))
    );
  }

  // --- 5. Get Record Details ---
  getRecordDetails(module: string, recordId: string): Observable<RecordDetails> {
    // Calls action_GetRecordDetails
    const url = this.buildApiUrl('GetRecordDetails', {
      module: module,
      record_id: recordId
    });
    return this.http.get<any>(url).pipe(
      map(response => response.data || {}),
      catchError(() => of({}))
    );
  }

  // --- 6. Send Text Message ---
  sendMessage(
    phoneNumber: string,
    message: string,
    channelId: number,
    moduleType?: string,
    moduleId?: string,
    replyToMessageId?: string
  ): Observable<any> {
    // Calls action_SendWhatsApp
    const url = this.buildApiUrl('SendWhatsApp');
    const payload: any = {
      to: this.cleanPhoneNumber(phoneNumber),
      type: 'text',
      channel_id: channelId,
      text: { body: message }
    };

    if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;
    if (moduleType) payload.parent_type = moduleType;
    if (moduleId) payload.parent_id = moduleId;

    return this.http.post(url, payload).pipe(
      catchError((error) => of({ error: true, message: error.error?.message || 'Server Error' }))
    );
  }

  // --- 7. Send Media Message ---
  sendMediaMessage(
    phoneNumber: string,
    mediaType: 'image' | 'document' | 'video' | 'audio',
    mediaUrl: string,
    channelId: number,
    caption?: string,
    filename?: string,
    moduleType?: string,
    moduleId?: string,
    replyToMessageId?: string
  ): Observable<any> {
    
    // Calls action_SendWhatsApp
    const url = this.buildApiUrl('SendWhatsApp');
    const payload: any = {
      to: this.cleanPhoneNumber(phoneNumber),
      type: mediaType, 
      channel_id: channelId,
      media: {
        url: mediaUrl,
        caption: caption || '',
        filename: filename || ''
      },
      text: { body: caption || '' }
    };

    if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;
    if (moduleType) payload.parent_type = moduleType;
    if (moduleId) payload.parent_id = moduleId;

    return this.http.post(url, payload).pipe(
      catchError((error) => of({ error: true, message: error.error?.message || 'Server Error' }))
    );
  }

  // --- 8. Send Template Message ---
  sendTemplateMessage(
    phoneNumber: string,
    templateId: string | number,
    channelId: number,
    parameters?: TemplateParameter[],
    moduleType?: string,
    moduleId?: string,
    previewText?: string
  ): Observable<any> {
    // Calls action_SendWhatsApp
    const url = this.buildApiUrl('SendWhatsApp');
    const payload: any = {
      to: this.cleanPhoneNumber(phoneNumber),
      type: 'template',
      template_id: Number(templateId) || templateId,
      channel_id: channelId,
      resolve_from_contact: false,
      preview_message: previewText
    };

    if (parameters && parameters.length > 0) {
      payload.parameters = {
        body: parameters.map(p => ({
          type: "text",
          value: (p.value || '').trim()
        }))
      };
    }

    if (moduleType) payload.parent_type = moduleType;
    if (moduleId) payload.parent_id = moduleId;

    return this.http.post(url, payload).pipe(
      catchError((error) => of({ error: true, message: error.error?.message || 'Server Error' }))
    );
  }

  // --- 9. Upload Media ---
  uploadMedia(file: File): Observable<{ success: boolean; url: string; filename: string; message?: string }> {
    // Calls action_UploadMedia
    const url = this.buildApiUrl('UploadMedia');
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<any>(url, formData).pipe(
      catchError((error) => of({ success: false, url: '', filename: '', message: error.message || 'Upload Failed' }))
    );
  }
}